/**
 * PropertyStarCell
 * Renders the star rating for one AI-import plan property on the Review/Decision
 * steps. Four states, never a dead link:
 *   - matched property with a persisted rating → StarRating linking to /properties/:id
 *   - new/unmatched candidate with a held preview → StarRating opening an inline preview
 *   - preview still in flight → a small "rating…" indicator
 *   - unrated (no preview, error, or matched-but-never-rated) → nothing
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, X } from 'lucide-react';
import { StarRating } from '@/components/ui/StarRating';
import { Badge } from '@/components/ui/Badge';
import { useStarRating } from '@/hooks/useStarRating';
import type { AIImportPlanProperty } from '@/types/ai-import';
import type { CandidateRatingEntry } from '../candidateRatings';

const TIER_VARIANT: Record<string, 'green' | 'blue' | 'yellow' | 'orange'> = {
  A: 'green',
  B: 'blue',
  C: 'yellow',
  D: 'orange',
};

const fmtStars = (n: number) => String(Math.round(n * 100) / 100);

export function PropertyStarCell({
  property,
  candidate,
  className,
}: {
  property: AIImportPlanProperty;
  candidate?: CandidateRatingEntry;
  className?: string;
}) {
  const existingId =
    property.action === 'update' && property.existing_id ? property.existing_id : undefined;
  // Always call the hook (disabled when there's no id) to keep hook order stable.
  const { data: existing } = useStarRating(existingId);
  const [open, setOpen] = useState(false);

  // 1) Matched property that already has a persisted rating → link to its detail.
  if (existingId && existing && existing.stars != null) {
    return (
      <Link
        to={`/properties/${existingId}`}
        className={`group inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent-50 ${className ?? ''}`}
        title="View this property's quality rating"
      >
        <StarRating
          value={existing.stars}
          low={existing.stars_low}
          high={existing.stars_high}
          size="sm"
          showRangeBand
          showTooltip={false}
        />
        <ExternalLink className="h-3 w-3 text-gray-300 dark:text-gray-600 group-hover:text-accent-500" />
      </Link>
    );
  }

  // 2) Candidate preview (new property, or matched-but-never-rated).
  if (!candidate) return null;

  if (candidate.status === 'loading') {
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 ${className ?? ''}`}>
        <Loader2 className="h-3 w-3 animate-spin" />
        rating…
      </span>
    );
  }

  if (candidate.status === 'error' || !candidate.rating) return null;

  const r = candidate.rating;
  const upside = r.star_upside ?? null;

  return (
    <div className={`relative inline-flex ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent-50"
        aria-expanded={open}
        title="Preview quality rating (saved when you apply)"
      >
        <StarRating
          value={r.stars}
          low={r.stars_low}
          high={r.stars_high}
          size="sm"
          showRangeBand
          showTooltip={false}
        />
        {r.evidence_tier && (
          <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {r.evidence_tier}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            role="dialog"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Quality rating preview
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                aria-label="Close preview"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <StarRating
                value={r.stars}
                low={r.stars_low}
                high={r.stars_high}
                size="md"
                showRangeBand
                showValue
                showTooltip={false}
              />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {r.evidence_tier && (
                <Badge size="sm" variant={TIER_VARIANT[r.evidence_tier] ?? 'gray'}>
                  Evidence {r.evidence_tier}
                </Badge>
              )}
              {r.provisional && (
                <Badge size="sm" variant="outline">
                  Provisional
                </Badge>
              )}
              {upside != null && upside > 0 && (
                <Badge size="sm" variant="green">
                  +{fmtStars(upside)}★ achievable
                </Badge>
              )}
            </div>

            {r.rationale && (
              <p className="mt-2 line-clamp-4 text-xs leading-snug text-gray-600 dark:text-gray-400">{r.rationale}</p>
            )}

            <div className="mt-2 border-t border-gray-100 pt-2 text-xs leading-snug text-gray-400 dark:border-gray-700 dark:text-gray-500">
              Preview only — the saved rating is computed from the created property when you apply.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
